import { useEffect, useRef, useState } from 'react';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { SERVICE_DESK } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import { fetchEntitlementDetails } from '../../common/utils/index.tsx';
import { EDITION_KEY, ADMIN_HUB_URL } from './constants.tsx';

// Module-level cache for the billing URL - only used when feature gate is enabled
let billingUrlCache:
	| {
			data: { billingPageUrl: string | undefined };
			loading: boolean;
			error: Error | unknown | null;
			subscribers: Set<
				(result: {
					billingPageUrl: string | undefined;
					loading: boolean;
					error: Error | unknown | null;
				}) => void
			>;
			abortController?: AbortController;
	  }
	| undefined;

const getCcpTransactionAccountId = async (entitlementId: string) => {
	const entitlementDetails = await fetchEntitlementDetails(entitlementId);
	if (!entitlementDetails?.transactionAccountId) {
		throw Error('Could not get transactionAccountId');
	}
	return (entitlementDetails ?? {}).transactionAccountId;
};

/**
 * Clear the billing URL cache - primarily for testing purposes
 */
export const clearBillingUrlCache = () => {
	// Cancel any ongoing request when clearing cache
	if (billingUrlCache?.abortController) {
		billingUrlCache.abortController.abort();
	}
	billingUrlCache = undefined;
};

export const useGetJsmPremiumBillingUrl = () => {
	const [billingPageUrl, setBillingPageUrl] = useState<string | undefined>();
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | unknown | null>(null);

	const tenantContext = useTenantContext();
	const environment = useEnvironment();
	const { data: orgId } = useOrgId();
	const subscriberRef = useRef<
		| ((result: {
				billingPageUrl: string | undefined;
				loading: boolean;
				error: Error | unknown | null;
		  }) => void)
		| null
	>(null);
	const isMountedRef = useRef<boolean>(true);

	useEffect(() => {
		isMountedRef.current = true;

		if (fg('jsm-sequoia-cache-billing-url')) {
			// CACHED VERSION - New behavior with caching
			// Create subscriber function for this hook instance
			const subscriber = (result: {
				billingPageUrl: string | undefined;
				loading: boolean;
				error: Error | unknown | null;
			}) => {
				// Only update state if component is still mounted
				if (isMountedRef.current) {
					setBillingPageUrl(result.billingPageUrl);
					setLoading(result.loading);
					setError(result.error);
				}
			};
			subscriberRef.current = subscriber;

			// If we have cached data, use it immediately
			if (billingUrlCache) {
				subscriber({
					billingPageUrl: billingUrlCache.data.billingPageUrl,
					loading: billingUrlCache.loading,
					error: billingUrlCache.error,
				});

				// Add this subscriber to the set
				billingUrlCache.subscribers.add(subscriber);

				return () => {
					isMountedRef.current = false;
					// Clean up subscriber on unmount
					if (billingUrlCache) {
						billingUrlCache.subscribers.delete(subscriber);
					}
				};
			}

			// Initialize cache and start the request
			const abortController = new AbortController();
			billingUrlCache = {
				data: { billingPageUrl: undefined },
				loading: true,
				error: null,
				subscribers: new Set([subscriber]),
				abortController,
			};

			const loadBillingInfo = async () => {
				try {
					// Check if request was aborted before proceeding
					if (abortController.signal.aborted) {
						return;
					}

					const productKey = SERVICE_DESK;
					const isUsingCcp =
						tenantContext.productEntitlementDetails?.[productKey]?.billingSourceSystem === 'CCP';
					const referrerKey = 'jsm';
					const commerceProductKey = `${productKey}.ondemand`;
					const entitlementId =
						tenantContext.productEntitlementDetails?.[productKey]?.entitlementId;
					const edition = EDITION_KEY.PREMIUM_EDITION;
					const adminHubBaseUrl = ADMIN_HUB_URL[environment];

					let resultUrl: string;
					if (isUsingCcp) {
						// CCP Billing System
						if (!entitlementId) {
							throw new Error('Empty entitlement ID');
						}

						// Check abort before async operation
						if (abortController.signal.aborted) {
							return;
						}

						const ccpTransactionAccountId = await getCcpTransactionAccountId(entitlementId);

						// Check abort after async operation
						if (abortController.signal.aborted) {
							return;
						}

						const ccpBaseUrl = fg('benifits_modal_billing_page_url_change')
							? `${adminHubBaseUrl}/billing/${ccpTransactionAccountId}/entitlement/${entitlementId}/change-plan?referrer=${referrerKey}`
							: `${adminHubBaseUrl}/billing/o/${orgId}/${ccpTransactionAccountId}/entitlement/${entitlementId}/change-plan?referrer=${referrerKey}`;
						resultUrl = `${ccpBaseUrl}&offeringName=${edition}`;
					} else {
						// HAMS Billing System
						resultUrl = `${adminHubBaseUrl}/s/${tenantContext.cloudId}/billing/applications/change-edition/${commerceProductKey}/${edition}`;
					}

					// Update cache and notify all subscribers
					if (billingUrlCache && !abortController.signal.aborted) {
						billingUrlCache.data.billingPageUrl = resultUrl;
						billingUrlCache.loading = false;
						billingUrlCache.error = null;

						// Only notify subscribers that are still mounted
						billingUrlCache.subscribers.forEach((sub) => {
							sub({
								billingPageUrl: resultUrl,
								loading: false,
								error: null,
							});
						});
					}
				} catch (e) {
					// Update cache with error and notify all subscribers
					if (billingUrlCache && !abortController.signal.aborted) {
						billingUrlCache.data.billingPageUrl = undefined;
						billingUrlCache.loading = false;
						billingUrlCache.error = e;

						billingUrlCache.subscribers.forEach((sub) => {
							sub({
								billingPageUrl: undefined,
								loading: false,
								error: e,
							});
						});
					}

					fireErrorAnalytics({
						meta: {
							id: 'useGetJsmPremiumBillingUrl',
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						error: e instanceof Error ? e : new Error(String(e)),
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			};

			loadBillingInfo();

			return () => {
				isMountedRef.current = false;
				// Cancel the request if it's still ongoing
				abortController.abort();
				// Clean up subscriber on unmount
				if (billingUrlCache && subscriberRef.current) {
					billingUrlCache.subscribers.delete(subscriberRef.current);
				}
			};
		}

		// ORIGINAL VERSION - Legacy behavior without caching
		const loadBillingInfo = async () => {
			try {
				const productKey = SERVICE_DESK;
				const isUsingCcp =
					tenantContext.productEntitlementDetails?.[productKey]?.billingSourceSystem === 'CCP';
				const referrerKey = 'jsm';
				const commerceProductKey = `${productKey}.ondemand`;
				const entitlementId = tenantContext.productEntitlementDetails?.[productKey]?.entitlementId;
				const edition = EDITION_KEY.PREMIUM_EDITION;
				const adminHubBaseUrl = ADMIN_HUB_URL[environment];

				if (isUsingCcp) {
					// CCP Billing System
					if (!entitlementId) {
						throw new Error('Empty entitlement ID');
					}

					const ccpTransactionAccountId = await getCcpTransactionAccountId(entitlementId);

					const ccpBaseUrl = fg('benifits_modal_billing_page_url_change')
						? `${adminHubBaseUrl}/billing/${ccpTransactionAccountId}/entitlement/${entitlementId}/change-plan?referrer=${referrerKey}`
						: `${adminHubBaseUrl}/billing/o/${orgId}/${ccpTransactionAccountId}/entitlement/${entitlementId}/change-plan?referrer=${referrerKey}`;

					setBillingPageUrl(`${ccpBaseUrl}&offeringName=${edition}`);
				} else {
					// HAMS Billing System
					setBillingPageUrl(
						`${adminHubBaseUrl}/s/${tenantContext.cloudId}/billing/applications/change-edition/${commerceProductKey}/${edition}`,
					);
				}
			} catch (e) {
				setError(e);
				fireErrorAnalytics({
					meta: {
						id: 'useGetJsmPremiumBillingUrl',
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					error: e instanceof Error ? e : new Error(String(e)),
					sendToPrivacyUnsafeSplunk: true,
				});
			} finally {
				setLoading(false);
			}
		};

		loadBillingInfo();
	}, [environment, orgId, tenantContext, tenantContext.cloudId]);

	return {
		data: {
			billingPageUrl,
		},
		loading,
		error,
	};
};
